import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Loader2, Sparkles, CreditCard, AlertTriangle, Package, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { TICKET_CATEGORY_OPTIONS, URGENCY_OPTIONS, CATEGORY_DESCRIPTIONS, formatCurrency } from '../lib/constants';

const CreateTicketPage = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    category: '',
    title: '',
    description: '',
    urgency: 'sedang',
    estimated_cost: 0,
    location_id: user?.location_id || '',
    attachments: [],
  });
  const [locations, setLocations] = useState([]);
  const [items, setItems] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]); // [{item_id, qty}]
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [locsRes, itemsRes, stocksRes] = await Promise.all([
        api.get('/locations'),
        api.get('/items', { params: { include_all: true } }),
        api.get('/warehouse/stock'),
      ]);
      setLocations(locsRes.data.filter((loc) => !loc.is_warehouse));
      setItems(itemsRes.data);
      setStocks(stocksRes.data);
    } catch (error) {
      console.error(error);
    }
  };

  const getStockAt = (itemId, locationId) => {
    const stock = stocks.find((s) => s.item_id === itemId && s.location_id === locationId);
    return stock?.quantity || 0;
  };

  const addItemToList = () => {
    setSelectedItems([...selectedItems, { item_id: '', qty: 1 }]);
  };

  const removeItemFromList = (idx) => {
    setSelectedItems(selectedItems.filter((_, i) => i !== idx));
  };

  const updateItemInList = (idx, field, value) => {
    const updated = [...selectedItems];
    updated[idx] = { ...updated[idx], [field]: value };
    setSelectedItems(updated);
  };

  const shoppingListTotal = selectedItems.reduce((sum, si) => {
    const item = items.find((i) => i.id === si.item_id);
    if (!item) return sum;
    return sum + (item.price * (parseInt(si.qty) || 0));
  }, 0);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (field === 'category' || field === 'title' || field === 'description') {
      setAiResult(null); // Reset AI estimate if category/desc changes
    }
  };

  const handleEstimateAI = async () => {
    if (!formData.title || !formData.description || !formData.category) {
      toast.error('Isi kategori, judul, dan deskripsi terlebih dahulu');
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await api.post('/tickets/estimate-cost', {
        category: formData.category,
        title: formData.title,
        description: formData.description,
      });
      setAiResult(data);
      setFormData({ ...formData, estimated_cost: data.estimated_cost });
      toast.success('Estimasi AI berhasil');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI estimasi gagal');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/tickets', {
        ...formData,
        estimated_cost: parseFloat(formData.estimated_cost) || 0,
      });
      
      // Allocate selected items to the newly created ticket
      const validItems = selectedItems.filter((si) => si.item_id && parseInt(si.qty) > 0);
      for (const si of validItems) {
        try {
          await api.post(`/tickets/${data.ticket_number}/allocate-item`, {
            item_id: si.item_id,
            quantity: parseInt(si.qty),
            from_location: 'PUSAT',
          });
        } catch (err) {
          const itemName = items.find((i) => i.id === si.item_id)?.name || 'Barang';
          toast.error(`Gagal alokasi ${itemName}: ${err.response?.data?.detail || 'error'}`);
        }
      }
      
      if (validItems.length > 0) {
        toast.success(`Tiket dibuat + ${validItems.length} barang dialokasi`);
      } else {
        toast.success('Tiket berhasil dibuat');
      }
      navigate(`/tickets/${data.ticket_number}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal membuat tiket');
    } finally {
      setLoading(false);
    }
  };

  const needsLocationSelector = !user?.location_id;
  // Kategori yang menyangkut aliran uang (invoice akan dibuat oleh Finance).
  const isFinancialCategory = [
    'petty_cash', 'refund_deposit', 'utility_payment', 'transfer_operasional',
    'petty_cash_in', 'petty_cash_out', 'deposit_in', 'deposit_out',
  ].includes(formData.category);
  const selectedLocation = locations.find((l) => l.id === formData.location_id);

  const confidenceColor = {
    low: 'bg-amber-100 text-amber-700 border-amber-300',
    medium: 'bg-blue-100 text-blue-700 border-blue-300',
    high: 'bg-green-100 text-green-700 border-green-300',
  };

  return (
    <Layout>
      <div className="max-w-3xl space-y-6" data-testid="create-ticket-page">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/tickets')}
            className="mb-4"
            data-testid="back-to-tickets-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Kembali ke Daftar Tiket
          </Button>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Buat Tiket</p>
          <h1 className="text-4xl font-medium tracking-tight">Tiket Baru</h1>
          <p className="text-muted-foreground mt-1">Isi form di bawah untuk membuat tiket permintaan baru</p>
        </div>

        <Card className="border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-xl tracking-tight font-medium">Informasi Tiket</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              {needsLocationSelector && (
                <div className="space-y-2">
                  <Label htmlFor="location">Lokasi Cabang *</Label>
                  <Select
                    value={formData.location_id}
                    onValueChange={(v) => handleChange('location_id', v)}
                    required
                  >
                    <SelectTrigger data-testid="ticket-location-select">
                      <SelectValue placeholder="Pilih lokasi cabang" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Kategori Tiket *</Label>
                  <Select value={formData.category} onValueChange={(v) => handleChange('category', v)} required>
                    <SelectTrigger data-testid="ticket-category-select">
                      <SelectValue placeholder="Pilih kategori" />
                    </SelectTrigger>
                    <SelectContent>
                      {TICKET_CATEGORY_OPTIONS.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.category && (
                    <p className="text-xs text-muted-foreground">
                      {CATEGORY_DESCRIPTIONS[formData.category]}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="urgency">Tingkat Urgensi *</Label>
                  <Select value={formData.urgency} onValueChange={(v) => handleChange('urgency', v)} required>
                    <SelectTrigger data-testid="ticket-urgency-select">
                      <SelectValue placeholder="Pilih urgensi" />
                    </SelectTrigger>
                    <SelectContent>
                      {URGENCY_OPTIONS.map((u) => (
                        <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Info: kategori keuangan — rekening tujuan dipilih saat Generate Invoice */}
              {isFinancialCategory && selectedLocation && (
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-4 h-4 text-primary" />
                    <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">
                      Info Pembayaran
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Rekening tujuan transfer & kategori pengeluaran akan dipilih oleh Finance saat <em>Generate Invoice</em>.
                    Daftar rekening dikelola di menu <span className="font-medium text-foreground">Daftar Rekening</span>.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="title">Judul Tiket *</Label>
                <Input
                  id="title"
                  placeholder="Contoh: AC di lobby tidak dingin"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  required
                  data-testid="ticket-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Deskripsi Detail *</Label>
                <Textarea
                  id="description"
                  placeholder="Jelaskan detail masalah, jenis barang/jasa yang dibutuhkan, dan lokasi spesifik..."
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  required
                  rows={6}
                  data-testid="ticket-description-input"
                />
              </div>

              {/* Shopping List - only for umum/it/me */}
              {['umum', 'it', 'me'].includes(formData.category) && (
                <div className="space-y-3 p-4 rounded-lg border border-primary/20 bg-primary/5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold flex items-center gap-2">
                        <Package className="w-4 h-4 text-primary" />
                        Barang yang Diperlukan (opsional)
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Barang diambil dari Gudang Pusat. Otomatis dialokasi ke tiket saat submit.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addItemToList}
                      data-testid="add-shopping-item-button"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Tambah
                    </Button>
                  </div>

                  {selectedItems.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-4">
                      Belum ada barang. Klik "Tambah" jika perlu barang dari gudang.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {selectedItems.map((si, idx) => {
                        const item = items.find((i) => i.id === si.item_id);
                        const stock = si.item_id ? getStockAt(si.item_id, 'PUSAT') : 0;
                        const subtotal = item ? item.price * (parseInt(si.qty) || 0) : 0;
                        return (
                          <div key={idx} className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                            <Select
                              value={si.item_id}
                              onValueChange={(v) => updateItemInList(idx, 'item_id', v)}
                            >
                              <SelectTrigger className="flex-1 min-w-0" data-testid={`shopping-item-select-${idx}`}>
                                <SelectValue placeholder="Pilih barang" />
                              </SelectTrigger>
                              <SelectContent>
                                {items.map((it) => {
                                  const s = getStockAt(it.id, 'PUSAT');
                                  return (
                                    <SelectItem key={it.id} value={it.id} disabled={s === 0}>
                                      {it.name} — {formatCurrency(it.price)}/{it.unit} (Stok PUSAT: {s})
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                            <Input
                              type="number"
                              min="1"
                              max={stock}
                              value={si.qty}
                              onChange={(e) => updateItemInList(idx, 'qty', e.target.value)}
                              className="w-20"
                              data-testid={`shopping-qty-${idx}`}
                            />
                            <div className="text-sm font-medium w-28 text-right whitespace-nowrap">
                              {formatCurrency(subtotal)}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItemFromList(idx)}
                              className="text-destructive"
                              data-testid={`remove-shopping-${idx}`}
                            >
                              ✕
                            </Button>
                          </div>
                        );
                      })}
                      <div className="flex justify-between pt-2 border-t border-primary/20">
                        <span className="text-sm font-semibold">Total Barang</span>
                        <span className="font-medium text-primary">{formatCurrency(shoppingListTotal)}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="estimated_cost">Estimasi Biaya (Rp)</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleEstimateAI}
                    disabled={aiLoading || !formData.category || !formData.title || !formData.description}
                    data-testid="ai-estimate-button"
                  >
                    {aiLoading ? (
                      <>
                        <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                        Estimasi...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3 mr-2 text-primary" />
                        Estimasi dengan AI
                      </>
                    )}
                  </Button>
                </div>
                <Input
                  id="estimated_cost"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={formData.estimated_cost}
                  onChange={(e) => handleChange('estimated_cost', e.target.value)}
                  data-testid="ticket-estimated-cost-input"
                />
                <p className="text-xs text-muted-foreground">Klik "Estimasi dengan AI" untuk mendapat estimasi otomatis dari harga pasar Indonesia</p>
              </div>

              {/* AI Estimate Result */}
              {aiResult && (
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/20 space-y-3" data-testid="ai-result">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      <p className="text-sm font-semibold">Estimasi AI</p>
                    </div>
                    <Badge variant="outline" className={confidenceColor[aiResult.confidence] || confidenceColor.medium}>
                      Confidence: {aiResult.confidence}
                    </Badge>
                  </div>
                  <p className="text-2xl font-medium tracking-tight text-primary">
                    {formatCurrency(aiResult.estimated_cost)}
                  </p>
                  {aiResult.reasoning && (
                    <p className="text-sm text-muted-foreground italic">"{aiResult.reasoning}"</p>
                  )}
                  {aiResult.breakdown && aiResult.breakdown.length > 0 && (
                    <div className="pt-3 border-t border-primary/20">
                      <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-2">Rincian</p>
                      <div className="space-y-1">
                        {aiResult.breakdown.map((b, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-foreground">
                              {b.item} <span className="text-muted-foreground">× {b.qty}</span>
                            </span>
                            <span className="font-medium">{formatCurrency(b.subtotal)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground pt-2 border-t border-primary/20">
                    * Estimasi berdasarkan harga pasar Indonesia. Bisa Anda ubah manual di kolom estimasi biaya.
                  </p>
                </div>
              )}

              <div className="flex items-center gap-3 pt-4 border-t border-border">
                <Button
                  type="submit"
                  className="bg-primary hover:bg-primary/90"
                  disabled={loading}
                  data-testid="submit-ticket-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Menyimpan...
                    </>
                  ) : (
                    'Buat Tiket'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/tickets')}
                  data-testid="cancel-ticket-button"
                >
                  Batal
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default CreateTicketPage;
